import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, forkJoin, map, of } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import {
  PaginatedResult,
  PaginationParams,
  createPaginatedResult,
} from '@core/common/models/pagination.models';
import {
  Product,
  ProductMutationResult,
  ProductSupplySourceInputValue,
  ProductVariantBatchMutationResult,
  ProductVariantMutationResult,
  SyncProductSupplySourcesResult,
} from '@features/catalog/models/product.models';
import {
  CreateProductBundleInput,
  ProductAttributeLinkMutationResult,
  ProductModifierLinkMutationResult,
  UpdateProductBundleInput,
  UpsertProductVariantsInput,
} from '@features/catalog/models/product-input.models';
import { ProductListFilters } from '@features/catalog/models/product-list-filters.models';
import {
  graphqlCategoryFilterVariables,
  normalizeVisibleInChannels,
} from '@features/catalog/utils/product-list-filters';
import {
  toGraphqlProductStructureFilter,
  toGraphqlVisibleInChannels,
} from '@features/catalog/utils/product-visibility.channels';
import { GRAPHQL_MEDIA_URLS_FIELDS, GRAPHQL_MEDIA_ASSET_URLS_FIELDS } from '@core/media/graphql/media-urls.fields';
import {
  GraphqlMutationProductVariantBatchPayloadDto,
  GraphqlMutationProductVariantPayloadDto,
  GraphqlMutationProductPayloadDto,
  GraphqlMutationSyncProductSupplySourcesPayloadDto,
  GraphqlProductByIdDataDto,
  GraphqlProductModifierGroupDto,
} from './dto/product.dto';
import {
  mapMutationProductPayload,
  mapMutationProductVariantPayload,
  mapProductDto,
  mapProductModifierGroupDto,
  mapProductVariantDto,
  mapSyncProductSupplySourcesPayload,
} from './mappers/product.mapper';
import { ensureNoTopLevelErrors, GraphqlHttpResponse, mapLifecycleMutationResult } from './mappers/graphql-error.mapper';
import { PreparationSettingsApiInput } from '@shared/ui/preparation-editor/preparation-editor.models';
import { mapCreateProductBundleInputToGraphql } from '@features/catalog/utils/product-bundle-graphql-input.mapper';

const MODIFIER_GROUPS_FIELDS = `
  modifierGroups(activeOnly: false) {
    id
    productModifierGroupDefinitionId
    name
    description
    selectionType
    required
    minSelections
    maxSelections
    position
    active
    options(activeOnly: false) {
      id
      productModifierOptionDefinitionId
      name
      description
      priceDelta
      variantPrices {
        variantId
        variantName
        variantPosition
        priceDelta
      }
      imageUrl
      ${GRAPHQL_MEDIA_URLS_FIELDS}
      maxQuantity
      position
      active
    }
  }
`;

const GET_PRODUCTS_QUERY = `
  query GetProductsPage(
    $activeOnly: Boolean = true
    $visibleInChannels: [ProductVisibilityChannel!]
    $productStructure: ProductStructureFilter
    $categoryIds: [ID!]
    $includeUncategorized: Boolean = false
    $attributeValueId: ID
    $attributeDefinitionId: ID
    $modifierGroupDefinitionId: ID
    $modifierOptionDefinitionId: ID
    $name: String
    $inventoryManaged: Boolean
    $limit: Int = 50
    $offset: Int = 0
  ) {
    products(
      activeOnly: $activeOnly
      visibleInChannels: $visibleInChannels
      productStructure: $productStructure
      categoryIds: $categoryIds
      includeUncategorized: $includeUncategorized
      attributeValueId: $attributeValueId
      attributeDefinitionId: $attributeDefinitionId
      modifierGroupDefinitionId: $modifierGroupDefinitionId
      modifierOptionDefinitionId: $modifierOptionDefinitionId
      name: $name
      inventoryManaged: $inventoryManaged
      limit: $limit
      offset: $offset
    ) {
      items {
        id
        name
        description
        price
        active
        sku
        imageUrl
        ${GRAPHQL_MEDIA_URLS_FIELDS}
        stockQuantity
        inventoryManaged
        posVisible
        publicVisible
        catalogBestSellerEnabled
        catalogBestSellerPosition
        catalogBadgeText
        fulfillmentLeadTimeMode
        fulfillmentLeadTimeDays
        fulfillmentReadyTime
        attributeValueVariantCount(attributeValueId: $attributeValueId)
      sendToKitchen
      allowHalfAndHalf
      prepRequiresMode
      prepTimeMinutes
      productType
      productStructure
      lockVersion
      categories {
          id
          name
          slug
        }
        productAttributeDefinitions(activeOnly: true) {
          id
          name
          slug
          productAttributeValues(activeOnly: true) {
            id
            value
          }
        }
        enabledProductAttributeValueIds
      }
      total
      limit
      offset
    }
  }
`;

const PRODUCT_BY_ID_QUERY = `
  query GetProductById($id: ID!) {
    product(id: $id) {
      id
      name
      description
      price
      active
      sku
      imageUrl
      ${GRAPHQL_MEDIA_URLS_FIELDS}
      galleryImages {
        id
        deliveryUrl
        position
        ${GRAPHQL_MEDIA_ASSET_URLS_FIELDS}
      }
      maxGalleryImages
      stockQuantity
      lowStockThreshold
      manageStock
      inventoryManaged
      inventoryItemId
      variantInventoryState
      posVisible
      publicVisible
      catalogBestSellerEnabled
      catalogBestSellerPosition
      catalogBadgeText
      fulfillmentLeadTimeMode
      fulfillmentLeadTimeDays
      fulfillmentReadyTime
      sendToKitchen
      allowHalfAndHalf
      prepRequiresMode
      prepTimeMinutes
      productType
      productStructure
      lockVersion
      weightGrams
      lengthCm
      widthCm
      heightCm
      isSupplyItem
      supplySources {
        id
        productVariantId
        provider { id name }
        supplyProduct { id name price }
      }
      categories {
        id
        name
        slug
      }
      productAttributeDefinitions(activeOnly: false) {
        id
        name
        slug
        active
        productAttributeValues(activeOnly: false) {
          id
          value
          active
        }
      }
      enabledProductAttributeValueIds
      ${MODIFIER_GROUPS_FIELDS}
      productVariants(activeOnly: true) {
        id
        productId
        companyId
        name
        sku
        externalReference
        imageUrl
        ${GRAPHQL_MEDIA_URLS_FIELDS}
        price
        stockQuantity
        lowStockThreshold
        inventoryManaged
        inventoryItemId
        position
        active
        attributesText
        weightGrams
        lengthCm
        widthCm
        heightCm
        productAttributeValues(activeOnly: false) {
          id
          value
          active
          productAttributeDefinition {
            id
            name
            active
          }
        }
      }
    }
  }
`;

const CREATE_PRODUCT_BUNDLE_MUTATION = `
  mutation CreateProductBundle($input: CreateProductBundleInput!) {
    createProductBundle(input: $input) {
      product {
        id
        name
        description
        price
        imageUrl
        sku
        stockQuantity
        lowStockThreshold
        manageStock
        inventoryManaged
        inventoryItemId
        variantInventoryState
        active
        posVisible
        publicVisible
        catalogBestSellerEnabled
        catalogBestSellerPosition
        catalogBadgeText
        fulfillmentLeadTimeMode
        fulfillmentLeadTimeDays
        fulfillmentReadyTime
        sendToKitchen
      allowHalfAndHalf
      prepRequiresMode
      prepTimeMinutes
        productType
        productStructure
        categories {
          id
          name
          slug
        }
        productAttributeDefinitions {
          id
          name
          productAttributeValues {
            id
            value
          }
        }
        enabledProductAttributeValueIds
        ${MODIFIER_GROUPS_FIELDS}
        productVariants {
          id
          name
          sku
          externalReference
          price
          stockQuantity
          lowStockThreshold
          inventoryManaged
          inventoryItemId
          position
          active
          attributesText
          imageUrl
          productAttributeValues {
            id
            value
            productAttributeDefinition {
              id
              name
            }
          }
        }
      }
      errors
    }
  }
`;

const UPDATE_PRODUCT_BUNDLE_MUTATION = `
  mutation UpdateProductBundle($id: ID!, $input: CreateProductBundleInput!) {
    updateProductBundle(id: $id, input: $input) {
      product {
        id
        name
        description
        price
        imageUrl
        sku
        stockQuantity
        lowStockThreshold
        manageStock
        inventoryManaged
        inventoryItemId
        variantInventoryState
        active
        posVisible
        publicVisible
        catalogBestSellerEnabled
        catalogBestSellerPosition
        catalogBadgeText
        fulfillmentLeadTimeMode
        fulfillmentLeadTimeDays
        fulfillmentReadyTime
        sendToKitchen
      allowHalfAndHalf
      prepRequiresMode
      prepTimeMinutes
        productType
        productStructure
        categories {
          id
          name
          slug
        }
        productAttributeDefinitions {
          id
          name
          productAttributeValues {
            id
            value
          }
        }
        enabledProductAttributeValueIds
        ${MODIFIER_GROUPS_FIELDS}
        productVariants {
          id
          name
          sku
          externalReference
          price
          stockQuantity
          lowStockThreshold
          inventoryManaged
          inventoryItemId
          position
          active
          attributesText
          imageUrl
          productAttributeValues {
            id
            value
            productAttributeDefinition {
              id
              name
            }
          }
        }
      }
      errors
    }
  }
`;

const DESTROY_PRODUCT_MUTATION = `
  mutation DestroyProduct($id: ID!) {
    destroyProduct(id: $id) {
      product {
        id
        name
        active
      }
      errors
    }
  }
`;

const UPDATE_PRODUCT_MUTATION = `
  mutation UpdateProduct($id: ID!, $active: Boolean, $posVisible: Boolean, $publicVisible: Boolean) {
    updateProduct(id: $id, active: $active, posVisible: $posVisible, publicVisible: $publicVisible) {
      product {
        id
        name
        active
        posVisible
        publicVisible
      }
      errors
    }
  }
`;

const UPDATE_PRODUCT_DIMENSIONS_MUTATION = `
  mutation UpdateProductDimensions(
    $id: ID!
    $weightGrams: Int
    $lengthCm: Float
    $widthCm: Float
    $heightCm: Float
  ) {
    updateProduct(
      id: $id
      weightGrams: $weightGrams
      lengthCm: $lengthCm
      widthCm: $widthCm
      heightCm: $heightCm
    ) {
      product {
        id
        name
        active
        weightGrams
        lengthCm
        widthCm
        heightCm
      }
      errors
    }
  }
`;

const SYNC_PRODUCT_SUPPLY_SOURCES_MUTATION = `
  mutation SyncProductSupplySources($productId: ID!, $supplySources: [ProductSupplySourceInput!]!) {
    syncProductSupplySources(productId: $productId, supplySources: $supplySources) {
      product {
        id
        isSupplyItem
      }
      supplySources {
        id
        productVariantId
        provider { id name }
        supplyProduct { id name price }
      }
      errors
    }
  }
`;

const PERMANENTLY_DELETE_PRODUCT_MUTATION = `
  mutation PermanentlyDeleteProduct($id: ID!) {
    permanentlyDeleteProduct(id: $id) {
      success
      errors
    }
  }
`;

const UPSERT_PRODUCT_VARIANTS_MUTATION = `
  mutation UpsertProductVariants($productId: ID!, $variants: [ProductVariantInputType!]!) {
    upsertProductVariants(productId: $productId, variants: $variants) {
      variants {
        id
        productId
        companyId
        name
        sku
        externalReference
        imageUrl
        ${GRAPHQL_MEDIA_URLS_FIELDS}
        price
        stockQuantity
        lowStockThreshold
        inventoryManaged
        inventoryItemId
        position
        active
        attributesText
        productAttributeValues(activeOnly: true) {
          id
          value
          productAttributeDefinition {
            id
            name
          }
        }
      }
      variantIds
      errors
    }
  }
`;

const DESTROY_PRODUCT_VARIANT_MUTATION = `
  mutation DestroyProductVariant($id: ID!) {
    destroyProductVariant(id: $id) {
      variant {
        id
        productId
        companyId
        name
        sku
        externalReference
        imageUrl
        ${GRAPHQL_MEDIA_URLS_FIELDS}
        price
        stockQuantity
        lowStockThreshold
        inventoryManaged
        inventoryItemId
        position
        active
        attributesText
        productAttributeValues(activeOnly: true) {
          id
          value
          productAttributeDefinition {
            id
            name
          }
        }
      }
      errors
    }
  }
`;

const REMOVE_PRODUCT_ATTRIBUTE_DEFINITION_MUTATION = `
  mutation RemoveProductAttributeDefinition($productId: ID!, $productAttributeDefinitionId: ID!) {
    removeProductAttributeDefinition(
      productId: $productId,
      productAttributeDefinitionId: $productAttributeDefinitionId
    ) {
      removedSelections
      errors
    }
  }
`;

const REMOVE_PRODUCT_VARIANT_ATTRIBUTE_VALUE_MUTATION = `
  mutation RemoveProductVariantAttributeValue($productVariantId: ID!, $productAttributeValueId: ID!) {
    removeProductVariantAttributeValue(
      productVariantId: $productVariantId,
      productAttributeValueId: $productAttributeValueId
    ) {
      errors
    }
  }
`;

const ASSIGN_PRODUCT_MODIFIER_GROUP_DEFINITION_MUTATION = `
  mutation AssignProductModifierGroupDefinition($productId: ID!, $productModifierGroupDefinitionId: ID!) {
    assignProductModifierGroupDefinition(
      productId: $productId
      productModifierGroupDefinitionId: $productModifierGroupDefinitionId
    ) {
      group {
        id
        productModifierGroupDefinitionId
        name
        description
        selectionType
        required
        minSelections
        maxSelections
        position
        active
        options(activeOnly: true) {
          id
          productModifierOptionDefinitionId
          name
          description
          priceDelta
          variantPrices {
            variantId
            variantName
            variantPosition
            priceDelta
          }
          imageUrl
          ${GRAPHQL_MEDIA_URLS_FIELDS}
          maxQuantity
          position
          active
        }
      }
      errors
    }
  }
`;

const REMOVE_PRODUCT_MODIFIER_GROUP_DEFINITION_MUTATION = `
  mutation RemoveProductModifierGroupDefinition($productId: ID!, $productModifierGroupDefinitionId: ID!) {
    removeProductModifierGroupDefinition(
      productId: $productId
      productModifierGroupDefinitionId: $productModifierGroupDefinitionId
    ) {
      group {
        id
      }
      errors
    }
  }
`;

const DESTROY_PRODUCT_MODIFIER_GROUP_MUTATION = `
  mutation DestroyProductModifierGroup($productId: ID!, $productModifierGroupId: ID!) {
    destroyProductModifierGroup(
      productId: $productId
      productModifierGroupId: $productModifierGroupId
    ) {
      group {
        id
      }
      errors
    }
  }
`;

const BULK_UPDATE_PRODUCTS_PREPARATION_MUTATION = `
  mutation UpdateProductsPreparation($productIds: [ID!]!, $settings: PreparationSettingsInput!) {
    updateProductsPreparation(productIds: $productIds, settings: $settings) {
      products { id }
      errors
    }
  }
`;

@Injectable()
export class ProductApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  private graphqlUrl(): string {
    return `${this.baseUrl}${ApiEndpoints.graphql}`;
  }

  private topLevelErrorMessages<T>(res: GraphqlHttpResponse<T>): string[] {
    return (res.errors ?? []).map((e) => e.message).filter(Boolean);
  }

  private mergeUniqueErrors(primary: string[], secondary: string[]): string[] {
    return [...new Set([...primary, ...secondary].map((msg) => msg.trim()).filter(Boolean))];
  }

  private mapProductMutationWithTopLevelErrors(
    payload: GraphqlMutationProductPayloadDto | null | undefined,
    topLevelErrors: string[]
  ): ProductMutationResult {
    const mapped = mapMutationProductPayload(payload);
    return {
      product: mapped.product,
      errors: this.mergeUniqueErrors(mapped.errors, topLevelErrors),
    };
  }

  private mapProductVariantMutationWithTopLevelErrors(
    payload: GraphqlMutationProductVariantPayloadDto | null | undefined,
    topLevelErrors: string[]
  ): ProductVariantMutationResult {
    const mapped = mapMutationProductVariantPayload(payload);
    return {
      variant: mapped.variant,
      errors: this.mergeUniqueErrors(mapped.errors, topLevelErrors),
    };
  }

  private mapProductVariantBatchMutationWithTopLevelErrors(
    payload: GraphqlMutationProductVariantBatchPayloadDto | null | undefined,
    topLevelErrors: string[]
  ): ProductVariantBatchMutationResult {
    const variants = (payload?.variants ?? []).map(mapProductVariantDto);
    const variantIds = (payload?.variantIds ?? []).map((id) => String(id));
    const errors = this.mergeUniqueErrors(
      Array.isArray(payload?.errors)
        ? payload?.errors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
        : [],
      topLevelErrors
    );

    return { variants, variantIds, errors };
  }

  list(
    filters?: ProductListFilters,
    pagination?: PaginationParams
  ): Observable<PaginatedResult<Product>> {
    const limit = pagination?.limit ?? 50;
    const offset = pagination?.offset ?? 0;
    const categoryFilter = graphqlCategoryFilterVariables(filters?.categoryIds);
    const variables: Record<string, unknown> = {
      activeOnly:
        filters?.activeOnly === undefined ? true : filters.activeOnly,
      categoryIds: categoryFilter.categoryIds,
      includeUncategorized: categoryFilter.includeUncategorized,
      attributeValueId: filters?.attributeValueId?.trim() || null,
      attributeDefinitionId: filters?.attributeDefinitionId?.trim() || null,
      modifierGroupDefinitionId: filters?.modifierGroupDefinitionId?.trim() || null,
      modifierOptionDefinitionId: filters?.modifierOptionDefinitionId?.trim() || null,
      limit,
      offset,
    };
    const visibleInChannels = toGraphqlVisibleInChannels(
      normalizeVisibleInChannels(filters)
    );
    if (visibleInChannels) {
      variables['visibleInChannels'] = visibleInChannels;
    }
    const productStructure = toGraphqlProductStructureFilter(filters?.productStructure ?? null);
    if (productStructure) {
      variables['productStructure'] = productStructure;
    }
    const nameTerm = filters?.name?.trim();
    if (nameTerm) {
      variables['name'] = nameTerm;
    }
    if (filters?.inventoryManaged === true || filters?.inventoryManaged === false) {
      variables['inventoryManaged'] = filters.inventoryManaged;
    }
    return this.http
      .post<GraphqlHttpResponse<{ products: { items: any[]; total: number; limit: number; offset: number } }>>(
        this.graphqlUrl(),
        {
          query: GET_PRODUCTS_QUERY,
          variables,
        }
      )
      .pipe(
        map((res) => ensureNoTopLevelErrors(res)),
        map((data) => {
          const products = (data.products?.items ?? []).map(mapProductDto);
          return createPaginatedResult({
            items: products,
            total: data.products?.total ?? 0,
            limit: data.products?.limit ?? limit,
            offset: data.products?.offset ?? offset,
          });
        })
      );
  }

  /** Búsqueda parcial por nombre usando los mismos filtros que `list()`. */
  searchByName(name: string, listFilters?: ProductListFilters): Observable<PaginatedResult<Product>> {
    const term = name.trim();
    return this.list({
      ...(listFilters ?? {}),
      name: term || null,
    });
  }

  getById(id: string): Observable<Product | null> {
    return this.http
      .post<GraphqlHttpResponse<GraphqlProductByIdDataDto>>(this.graphqlUrl(), {
        query: PRODUCT_BY_ID_QUERY,
        variables: { id },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res)),
        map((data) => (data.product ? mapProductDto(data.product) : null))
      );
  }

  createBundle(input: CreateProductBundleInput): Observable<ProductMutationResult> {
    const graphqlInput = mapCreateProductBundleInputToGraphql(input);
    return this.http
      .post<
        GraphqlHttpResponse<{
          createProductBundle?: GraphqlMutationProductPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: CREATE_PRODUCT_BUNDLE_MUTATION,
        variables: { input: graphqlInput },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductMutationWithTopLevelErrors(
            data.createProductBundle,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  updateBundle(input: UpdateProductBundleInput): Observable<ProductMutationResult> {
    const graphqlInput = mapCreateProductBundleInputToGraphql(input.input);
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductBundle?: GraphqlMutationProductPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPDATE_PRODUCT_BUNDLE_MUTATION,
        variables: {
          id: input.id,
          input: graphqlInput,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductMutationWithTopLevelErrors(
            data.updateProductBundle,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  destroy(id: string): Observable<ProductMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProduct?: GraphqlMutationProductPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: DESTROY_PRODUCT_MUTATION,
        variables: { id },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductMutationWithTopLevelErrors(
            data.destroyProduct,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  updateProduct(
    id: string,
    changes: { active?: boolean; posVisible?: boolean; publicVisible?: boolean }
  ): Observable<ProductMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProduct?: GraphqlMutationProductPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPDATE_PRODUCT_MUTATION,
        variables: { id, ...changes },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductMutationWithTopLevelErrors(
            data.updateProduct,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  updateActive(id: string, active: boolean): Observable<ProductMutationResult> {
    return this.updateProduct(id, { active });
  }

  bulkUpdateProducts(
    ids: readonly string[],
    changes: { active?: boolean; posVisible?: boolean; publicVisible?: boolean }
  ): Observable<{ updatedCount: number; errors: string[] }> {
    if (!ids.length) {
      return of({ updatedCount: 0, errors: [] });
    }
    const requests = ids.map((id) => this.updateProduct(id, changes));
    return forkJoin(requests).pipe(
      map((results) => {
        let updatedCount = 0;
        const errors: string[] = [];
        for (const res of results) {
          if (res.errors.length) {
            errors.push(...res.errors);
          } else {
            updatedCount++;
          }
        }
        return { updatedCount, errors: [...new Set(errors)] };
      })
    );
  }

  updateDimensions(
    id: string,
    dimensions: {
      weightGrams?: number | null;
      lengthCm?: number | null;
      widthCm?: number | null;
      heightCm?: number | null;
    }
  ): Observable<ProductMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProduct?: GraphqlMutationProductPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPDATE_PRODUCT_DIMENSIONS_MUTATION,
        variables: {
          id,
          weightGrams: dimensions.weightGrams ?? null,
          lengthCm: dimensions.lengthCm ?? null,
          widthCm: dimensions.widthCm ?? null,
          heightCm: dimensions.heightCm ?? null,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductMutationWithTopLevelErrors(
            data.updateProduct,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          permanentlyDeleteProduct?: { success?: boolean; errors?: string[] } | null;
        }>
      >(this.graphqlUrl(), {
        query: PERMANENTLY_DELETE_PRODUCT_MUTATION,
        variables: { id },
      })
      .pipe(map((res) => mapLifecycleMutationResult(res, 'permanentlyDeleteProduct')));
  }

  upsertVariants(input: UpsertProductVariantsInput): Observable<ProductVariantBatchMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          upsertProductVariants?: GraphqlMutationProductVariantBatchPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPSERT_PRODUCT_VARIANTS_MUTATION,
        variables: {
          productId: input.productId,
          variants: input.variants,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductVariantBatchMutationWithTopLevelErrors(
            data.upsertProductVariants,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  destroyVariant(id: string): Observable<ProductVariantMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProductVariant?: GraphqlMutationProductVariantPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: DESTROY_PRODUCT_VARIANT_MUTATION,
        variables: { id },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          return this.mapProductVariantMutationWithTopLevelErrors(
            data.destroyProductVariant,
            this.topLevelErrorMessages(res)
          );
        })
      );
  }

  removeAttributeDefinitionFromProduct(input: {
    productId: string;
    productAttributeDefinitionId: string;
  }): Observable<ProductAttributeLinkMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          removeProductAttributeDefinition?: {
            removedSelections?: number | null;
            errors?: unknown;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: REMOVE_PRODUCT_ATTRIBUTE_DEFINITION_MUTATION,
        variables: {
          productId: input.productId,
          productAttributeDefinitionId: input.productAttributeDefinitionId,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          const payload = data.removeProductAttributeDefinition;
          const payloadErrors = payload?.errors;
          const errors = this.mergeUniqueErrors(
            Array.isArray(payloadErrors)
              ? payloadErrors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
              : [],
            this.topLevelErrorMessages(res)
          );

          return {
            errors,
            removedSelections: payload?.removedSelections ?? 0,
          };
        })
      );
  }

  removeAttributeValueFromVariant(input: {
    productVariantId: string;
    productAttributeValueId: string;
  }): Observable<ProductAttributeLinkMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          removeProductVariantAttributeValue?: {
            errors?: unknown;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: REMOVE_PRODUCT_VARIANT_ATTRIBUTE_VALUE_MUTATION,
        variables: {
          productVariantId: input.productVariantId,
          productAttributeValueId: input.productAttributeValueId,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          const payload = data.removeProductVariantAttributeValue;
          const payloadErrors = payload?.errors;
          const errors = this.mergeUniqueErrors(
            Array.isArray(payloadErrors)
              ? payloadErrors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
              : [],
            this.topLevelErrorMessages(res)
          );

          return { errors };
        })
      );
  }

  assignModifierGroupDefinitionToProduct(input: {
    productId: string;
    productModifierGroupDefinitionId: string;
  }): Observable<ProductModifierLinkMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          assignProductModifierGroupDefinition?: {
            group?: GraphqlProductModifierGroupDto | null;
            errors?: unknown;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: ASSIGN_PRODUCT_MODIFIER_GROUP_DEFINITION_MUTATION,
        variables: {
          productId: input.productId,
          productModifierGroupDefinitionId: input.productModifierGroupDefinitionId,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          const payload = data.assignProductModifierGroupDefinition;
          const payloadErrors = payload?.errors;
          const errors = this.mergeUniqueErrors(
            Array.isArray(payloadErrors)
              ? payloadErrors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
              : [],
            this.topLevelErrorMessages(res)
          );

          return {
            errors,
            group: payload?.group ? mapProductModifierGroupDto(payload.group) : null,
          };
        })
      );
  }

  removeModifierGroupDefinitionFromProduct(input: {
    productId: string;
    productModifierGroupDefinitionId: string;
  }): Observable<ProductModifierLinkMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          removeProductModifierGroupDefinition?: {
            group?: { id: string | number } | null;
            errors?: unknown;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: REMOVE_PRODUCT_MODIFIER_GROUP_DEFINITION_MUTATION,
        variables: {
          productId: input.productId,
          productModifierGroupDefinitionId: input.productModifierGroupDefinitionId,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          const payload = data.removeProductModifierGroupDefinition;
          const payloadErrors = payload?.errors;
          const errors = this.mergeUniqueErrors(
            Array.isArray(payloadErrors)
              ? payloadErrors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
              : [],
            this.topLevelErrorMessages(res)
          );

          return { errors, group: null };
        })
      );
  }

  destroyProductModifierGroup(input: {
    productId: string;
    productModifierGroupId: string;
  }): Observable<ProductModifierLinkMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProductModifierGroup?: {
            group?: { id: string | number } | null;
            errors?: unknown;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: DESTROY_PRODUCT_MODIFIER_GROUP_MUTATION,
        variables: {
          productId: input.productId,
          productModifierGroupId: input.productModifierGroupId,
        },
      })
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          const payload = data.destroyProductModifierGroup;
          const payloadErrors = payload?.errors;
          const errors = this.mergeUniqueErrors(
            Array.isArray(payloadErrors)
              ? payloadErrors.map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
              : [],
            this.topLevelErrorMessages(res)
          );

          return { errors, group: null };
        })
      );
  }

  bulkUpdatePreparation(
    ids: readonly string[],
    input: PreparationSettingsApiInput
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductsPreparation?: {
            products?: Array<{ id?: string | null } | null> | null;
            errors?: string[] | null;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: BULK_UPDATE_PRODUCTS_PREPARATION_MUTATION,
        variables: { productIds: [...ids], settings: input },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const payload = data.updateProductsPreparation;
          return {
            updatedCount: payload?.products?.length ?? 0,
            errors: payload?.errors ?? [],
          };
        })
      );
  }

  /**
   * `createProductBundle`/`updateProductBundle` no aceptan proveedores de insumo: se
   * persisten con una mutación aparte una vez que el producto (y sus variantes) tienen id,
   * igual que `updateDimensions`.
   */
  syncSupplySources(
    productId: string,
    supplySources: readonly ProductSupplySourceInputValue[]
  ): Observable<SyncProductSupplySourcesResult> {
    return this.http
      .post<GraphqlHttpResponse<{ syncProductSupplySources?: GraphqlMutationSyncProductSupplySourcesPayloadDto | null }>>(
        this.graphqlUrl(),
        {
          query: SYNC_PRODUCT_SUPPLY_SOURCES_MUTATION,
          variables: {
            productId,
            supplySources: supplySources.map((source) => ({
              providerId: source.providerId,
              purchasePrice: source.purchasePrice,
              variantPrices: (source.variantPrices ?? []).map((variantPrice) => ({
                productVariantId: variantPrice.productVariantId,
                purchasePrice: variantPrice.purchasePrice,
              })),
            })),
          },
        }
      )
      .pipe(
        map((res) => {
          const data = ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true });
          const mapped = mapSyncProductSupplySourcesPayload(data.syncProductSupplySources);
          return { ...mapped, errors: this.mergeUniqueErrors(mapped.errors, this.topLevelErrorMessages(res)) };
        })
      );
  }
}
